import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  InputAdornment,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useForm, useWatch, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  FormProvider,
  FormTextField,
  FormSelectField,
  FormDatePicker,
} from '../../../components/forms';

import {
  DiscountCode,
  createDiscountCode,
  updateDiscountCode
} from '../../../services/discountCodeService';

interface DiscountCodeFormDialogProps {
  open: boolean;
  onClose: (success?: boolean, isEdit?: boolean) => void;
  discountCode: DiscountCode | null;
}

// Form data type
interface DiscountCodeFormData {
  code: string;
  description: string;
  discountType: 'fixed' | 'percentage';
  discountValue: number;
  maxUses: number | null;
  isActive: boolean;
  expiresAt: Date | null;
}

// Form validation schema
const discountCodeSchema = yup.object().shape({
  code: yup
    .string()
    .required('Discount code is required')
    .matches(/^[A-Za-z0-9_-]{3,}$/, 
      'Code must be at least 3 characters with only letters, numbers, dashes, and underscores'),
  description: yup.string().required('Description is required'),
  discountType: yup.string().oneOf(['fixed', 'percentage']).required('Discount type is required'),
  discountValue: yup
    .number()
    .required('Discount value is required')
    .positive('Discount value must be greater than 0')
    .when('discountType', {
      is: 'percentage',
      then: (schema) => schema.max(100, 'Percentage discount cannot exceed 100%'),
    }),
  maxUses: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .test('positive-or-null', 'Maximum uses must be greater than 0', 
      value => value === null || (value !== undefined && value > 0)),
  isActive: yup.boolean().required(),
  expiresAt: yup.date().nullable(),
});

const DiscountCodeFormDialog: React.FC<DiscountCodeFormDialogProps> = ({
  open,
  onClose,
  discountCode
}) => {
  const isEditMode = !!discountCode;
  const dialogTitle = isEditMode ? 'Edit Discount Code' : 'Create Discount Code';
  
  // Form methods from React Hook Form with explicit typing
  const methods = useForm<DiscountCodeFormData>({
    resolver: yupResolver(discountCodeSchema) as unknown as Resolver<DiscountCodeFormData>,
    defaultValues: {
      code: '',
      description: '',
      discountType: 'percentage' as const,
      discountValue: 0,
      maxUses: null,
      isActive: true,
      expiresAt: null,
    }
  });
  
  const { 
    reset, 
    control, 
    setValue, 
    formState: { isSubmitting } 
  } = methods;
  
  // Watch the discount type to dynamically update the UI
  const discountType = useWatch({ control, name: 'discountType' });
  
  // State for API error handling
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  
  // Initialize form with discount code data if in edit mode
  useEffect(() => {
    if (isEditMode && discountCode) {
      reset({
        code: discountCode.code,
        description: discountCode.description,
        discountType: discountCode.discountType,
        discountValue: discountCode.discountValue,
        maxUses: discountCode.maxUses,
        isActive: discountCode.isActive,
        expiresAt: discountCode.expiresAt ? new Date(discountCode.expiresAt) : null,
      });
    } else {
      // Reset form for create mode
      reset({
        code: '',
        description: '',
        discountType: 'percentage',
        discountValue: 0,
        maxUses: null,
        isActive: true,
        expiresAt: null,
      });
    }
  }, [isEditMode, discountCode, reset]);
  
  // Generate random discount code
  const generateRandomCode = () => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoiding similar looking characters
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setValue('code', result, { shouldValidate: true });
  };
  
  // Handle form submission
  const onSubmit = async (data: DiscountCodeFormData) => {
    setSubmitError(null);

    try {
      const discountCodeData = {
        code: data.code,
        description: data.description,
        discountType: data.discountType,
        discountValue: data.discountValue,
        maxUses: data.maxUses,
        isActive: data.isActive,
        expiresAt: data.expiresAt ? new Date(data.expiresAt).toISOString() : null
      };

      if (isEditMode && discountCode) {
        await updateDiscountCode(discountCode.id, discountCodeData);
        onClose(true, true); // Success + Edit mode
      } else {
        await createDiscountCode(discountCodeData);
        onClose(true, false); // Success + Create mode
      }
    } catch (error) {
      console.error('Error saving discount code:', error);
      setSubmitError('Failed to save discount code. Please try again.');
    }
  };
  
  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <FormTextField
                name="code"
                label="Discount Code"
                disabled={isEditMode} // Can't edit code in edit mode
                InputProps={{
                  endAdornment: !isEditMode ? (
                    <InputAdornment position="end">
                      <Button
                        onClick={generateRandomCode}
                        size="small"
                        variant="text"
                      >
                        Generate
                      </Button>
                    </InputAdornment>
                  ) : null
                }}
                onChange={(e) => setValue('code', e.target.value.toUpperCase(), { shouldValidate: true })}
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormTextField
                name="description"
                label="Description"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormSelectField
                name="discountType"
                label="Discount Type"
                options={[
                  { value: 'percentage', label: 'Percentage (%)' },
                  { value: 'fixed', label: 'Fixed Amount ($)' }
                ]}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="discountValue"
                label="Discount Value"
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {discountType === 'percentage' ? '%' : '$'}
                    </InputAdornment>
                  ),
                  // For percentage, restrict to integers only
                  inputProps: discountType === 'percentage' 
                    ? { step: 1, min: 1, max: 100 } 
                    : { step: 0.01, min: 0.01 }
                }}
                onChange={(e) => {
                  // For percentages, force integer values
                  if (discountType === 'percentage' && e.target.value) {
                    const intValue = parseInt(e.target.value, 10);
                    if (!isNaN(intValue)) {
                      setValue('discountValue', intValue, { shouldValidate: true });
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="maxUses"
                label="Maximum Uses (Leave empty for unlimited)"
                type="number"
                helperText="Leave empty for unlimited uses"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormDatePicker
                name="expiresAt"
                label="Expiration Date (Optional)"
                helperText="Leave empty for no expiration"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => onClose(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default DiscountCodeFormDialog;